import { Job, JobPayType } from 'lib/types/jobs';

export interface PayValue {
  value?: number;
  minValue?: number;
  maxValue?: number;
  unitText?: PayType;
}

const jobToEmploymentMap = {
  pt: 'PART_TIME',
  ft: 'FULL_TIME',
  gig: 'CONTRACTOR',
  intern: 'INTERN',
};

export type PayType = 'HOUR' | 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'JOB';

const PayTypeMap: Record<string, PayType> = {
  hourly: 'HOUR',
  'one-time': 'JOB',
  annually: 'YEAR',
  monthly: 'MONTH',
  weekly: 'WEEK',
  daily: 'DAY',
};

function getStructuredPayValue(job: Job): PayValue | undefined {
  const payUnitText = PayTypeMap[job.payType as JobPayType];

  if (!payUnitText) {
    return undefined;
  }

  if (job.payMin && job.payMax) {
    return {
      minValue: job.payMin,
      maxValue: job.payMax,
      unitText: payUnitText,
    };
  }

  const basePay = job.payMin || job.payAmount;
  if (basePay) {
    return {
      value: basePay,
      unitText: payUnitText,
    };
  }
  return undefined;
}

export function getStructuredJobData(job: Job, altDescription: string) {
  const baseSalaryValue = getStructuredPayValue(job);

  const baseSalary = {
    '@type': 'MonetaryAmount',
    currency: 'USD',
    value: {
      ...baseSalaryValue,
      '@type': 'QuantitativeValue',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.name,
    description: job.description || altDescription,
    datePosted: job.createdAt as Date,
    jobLocationType: job.locationType === 'remote' ? 'TELECOMMUTE' : undefined,
    applicantLocationRequirements: job.locationType === 'remote' ? {
      '@type': 'Country',
      name: 'USA',
    } : undefined,
    jobLocation: {
      '@type': 'Place',
      address: { addressCountry: 'US' },
    },
    employmentType: jobToEmploymentMap[job.jobType as keyof typeof jobToEmploymentMap],
    hiringOrganization: {
      '@type': 'Organization',
      name: job.account.name,
    },
    baseSalary: baseSalaryValue ? baseSalary : undefined,
  };
}
